/**
 * Rooms Page
 * Display available rooms and selection
 */

import { t, formatDate, translateRatePlan, translateRatePlanDescription } from '../i18n';
import {
  Header,
  Card,
  RoomCard,
  GroupedRoomCard,
  formatPrice,
  type GroupedRoomType,
  ErrorMessage,
  FloatingNav,
  ExtraChargeModal,
  BackToTopButton,
  initBackToTop,
} from '../components';
import {
  getState,
  selectRoom,
  navigateTo,
  calculateNights,
  validateCapacityAndCalculateCharges,
  setExtraCharges,
} from '../store';
import { openLightbox } from '../components/lightbox';
import { renderCarousel } from '../components/carousel';
import type { RoomAvailability } from '../types';

/**
 * Group available rooms by roomTypeCode, sorting plans within each group by cheapest first
 */
export function groupRoomsByType(availableRooms: RoomAvailability[]): GroupedRoomType[] {
  const map = new Map<string, GroupedRoomType>();

  availableRooms.forEach((room, originalIndex) => {
    const key = room.roomTypeCode;
    if (!map.has(key)) {
      map.set(key, {
        roomTypeCode: room.roomTypeCode,
        roomTypeName: room.roomTypeName,
        description: room.description,
        bedType: room.bedType,
        bedTypeDesc: room.bedTypeDesc,
        amenities: room.amenities,
        available: room.available,
        plans: [],
      });
    }

    const group = map.get(key)!;
    group.plans.push({
      originalIndex,
      room,
    });
  });

  // Sort each group's plans by averageNightlyRate ascending (cheapest first)
  const result: GroupedRoomType[] = [];
  for (const group of map.values()) {
    group.plans.sort((a, b) => (a.room.averageNightlyRate || 0) - (b.room.averageNightlyRate || 0));
    result.push(group);
  }

  return result;
}

/**
 * Render rooms page
 */
export function renderRoomsPage(): string {
  const state = getState();
  const { search, availableRooms } = state;

  if (!search) {
    return ErrorMessage(t('errorGeneric'));
  }

  const nights = calculateNights(search.checkIn, search.checkOut);

  // Date summary
  const dateSummary = `
    <div class="date-summary">
      <div class="date-box">
        <span class="date-label">${t('checkIn')}</span>
        <span class="date-value">${formatDate(search.checkIn)}</span>
      </div>
      <div class="date-box">
        <span class="date-label">${t('checkOut')}</span>
        <span class="date-value">${formatDate(search.checkOut)}</span>
      </div>
      <div class="nights-badge">${nights} ${t('nights')}</div>
    </div>
  `;

  // No rooms available
  if (availableRooms.length === 0) {
    return `
      ${Header(t('roomsTitle'), false)}
      <div class="page-content">
        ${Card(dateSummary)}
        <div class="empty-state">
          <div class="empty-icon">🏨</div>
          <p>${t('noRoomsAvailable')}</p>
        </div>
      </div>
      ${FloatingNav()}
    `;
  }

  // Group rooms by roomTypeCode and render 1 card per room type with rate plan selector
  const groupedRooms = groupRoomsByType(availableRooms);
  const roomCards = groupedRooms
    .map((group: GroupedRoomType) => GroupedRoomCard(group, nights))
    .join('');

  // Disable next button if no room selected
  const disableNext = !state.selectedRoom;

  return `
    ${Header(t('roomsTitle'), false)}
    <div class="page-content">
      ${Card(dateSummary, 'sticky-summary')}
      <div class="rooms-list" id="rooms-list">
        ${roomCards}
      </div>
    </div>

    ${BackToTopButton('rooms')}

    ${FloatingNav(disableNext)}
  `;
}

/**
 * Initialize rooms page event handlers
 */
export function initRoomsPage(): void {
  const state = getState();

  // Back button handler
  window.handleBack = () => {
    navigateTo('search');
  };

  // Room selection handler with capacity validation
  window.selectRoom = (index: number) => {
    const room = state.availableRooms[index];
    if (!room || !state.search) return;

    selectRoom(room);

    // Validate capacity and calculate extra charges
    const extraCharges = validateCapacityAndCalculateCharges(room, state.search);

    if (extraCharges) {
      // Show confirmation modal
      setExtraCharges(extraCharges);
      showExtraChargeModal(extraCharges, room);
    } else {
      // No extra charges, proceed to guest info
      setExtraCharges(null);
      navigateTo('guest-info');
    }
  };

  // Expose forward action to footer: validate capacity and proceed to guest-info
  window.handleForward = () => {
    const current = getState();
    if (!current.selectedRoom || !current.search) {
      // No room selected, do nothing (button should be disabled)
      return;
    }

    // Validate capacity and calculate extra charges
    const extraCharges = validateCapacityAndCalculateCharges(current.selectedRoom, current.search);

    if (extraCharges) {
      // Show confirmation modal
      setExtraCharges(extraCharges);
      showExtraChargeModal(extraCharges, current.selectedRoom);
    } else {
      // No extra charges, proceed to guest info
      setExtraCharges(null);
      navigateTo('guest-info');
    }
  };

  // Floating "BACK TO TOP" for Rooms list
  initBackToTop('rooms');

  // Wire up toggle accordion buttons on each room card
  const toggleButtons = document.querySelectorAll<HTMLButtonElement>('.btn-toggle-rates');
  toggleButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const roomTypeCode = btn.getAttribute('data-room-type');
      if (!roomTypeCode) return;

      const container = document.getElementById(`rate-plans-${roomTypeCode}`);
      if (!container) return;

      const isExpanded = btn.classList.contains('expanded');
      const textSpan = btn.querySelector<HTMLElement>('.toggle-text');
      const iconSpan = btn.querySelector<HTMLElement>('.toggle-icon');
      const lowestText = btn.getAttribute('data-lowest-text') || '';
      const hideText = btn.getAttribute('data-hide-text') || '';

      if (isExpanded) {
        // Collapse
        container.style.display = 'none';
        btn.classList.remove('expanded');
        if (textSpan) textSpan.textContent = lowestText;
        if (iconSpan) iconSpan.textContent = '▾';
      } else {
        // Expand
        container.style.display = 'block';
        btn.classList.add('expanded');
        if (textSpan) textSpan.textContent = hideText;
        if (iconSpan) iconSpan.textContent = '▴';
      }
    });
  });

  // Load image carousels for each room card
  try {
    loadRoomCarousels();
  } catch (err) {
    console.warn('[Rooms] Failed to load room carousels', err);
  }
}

// Load and render carousels for each room card
async function loadRoomCarousels(): Promise<void> {
  try {
    const { getTenantId } = await import('../config');
    const tenantId = getTenantId();
    const containerEls = Array.from(document.querySelectorAll('.room-images')) as HTMLElement[];
    if (containerEls.length === 0) return;

    for (const el of containerEls) {
      const roomType = el.getAttribute('data-room-type');
      if (!roomType) continue;

      // Find matching room in state to get hotelCode
      const state = getState();
      const room = state.availableRooms.find(r => r.roomTypeCode === roomType);
      if (!room || !room.hotelCode) continue;

      try {
        const { getRoomImages } = await import('../api');
        const images = await getRoomImages(tenantId, room.hotelCode, roomType);
        if (images && images.length > 0) {
          renderCarousel(el, images.slice(0, 15));
        }
      } catch (err) {
        console.warn('[Rooms] Failed to fetch images for', roomType, err);
      }
    }
  } catch (err) {
    console.error('[Rooms] loadRoomCarousels error', err);
  }
}



// Show extra charge confirmation modal
function showExtraChargeModal(
  extraCharges: NonNullable<ReturnType<typeof validateCapacityAndCalculateCharges>>,
  room: RoomAvailability
) {
  const modalHtml = ExtraChargeModal(
    extraCharges.extraAdults,
    extraCharges.extraChildren,
    extraCharges.extraAdultCharge,
    extraCharges.extraChildCharge,
    extraCharges.totalExtraCharge,
    room.currency || 'THB',
    () => {}, // Placeholder, will be replaced by window handler
    () => {} // Placeholder, will be replaced by window handler
  );

  // Insert modal into body
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalHtml;
  document.body.appendChild(modalContainer);

  // Handle confirm: proceed to guest info
  window.handleExtraChargeConfirm = () => {
    document.body.removeChild(modalContainer);
    navigateTo('guest-info');
  };

  // Handle cancel: go back to search
  window.handleExtraChargeCancel = () => {
    document.body.removeChild(modalContainer);
    setExtraCharges(null);
    navigateTo('search');
  };
}

// Extend window for global handlers
declare global {
  interface Window {
    handleBack: () => void;
    selectRoom: (index: number) => void;
    handleForward?: () => void;
    handleExtraChargeConfirm?: () => void;
    handleExtraChargeCancel?: () => void;
  }
}
